import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';

/**
 * Google-style user avatar dropdown menu.
 * Shows user photo; click opens dropdown with name, email, and Sign Out.
 */
export default function UserMenu() {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    function handleClick(e) {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  if (!user) return null;

  const initials = (user.displayName || user.email || '?')
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

  return (
    <div ref={menuRef} className="relative">
      <button
        onClick={() => setOpen(!open)}
        className="w-8 h-8 rounded-full overflow-hidden border-2 border-transparent hover:border-red-400 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-red-400"
        aria-label="Account menu"
        title={user.displayName || user.email}
      >
        {user.photoURL ? (
          <img
            src={user.photoURL}
            alt={user.displayName || 'User'}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full bg-gray-600 flex items-center justify-center text-xs font-bold text-gray-200">
            {initials}
          </div>
        )}
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-56 bg-gradient-to-br from-gray-900 to-black rounded-md shadow-2xl z-50 border-2 border-red-700 overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-700">
            <p className="text-sm font-medium text-gray-100 truncate">
              {user.displayName || 'User'}
            </p>
            <p className="text-xs text-gray-400 truncate">
              {user.email}
            </p>
          </div>
          <div className="py-1">
            <button
              onClick={() => {
                setOpen(false);
                logout();
              }}
              className="w-full text-left px-4 py-2 text-sm text-gray-300 hover:bg-red-900 hover:text-white transition-colors duration-200"
            >
              Sign Out
            </button>
          </div>
          <div className="px-4 py-2 border-t border-gray-700 flex gap-3 text-xs text-gray-500">
            <Link to="/about" onClick={() => setOpen(false)} className="hover:text-gray-300 transition-colors">About</Link>
            <span>·</span>
            <Link to="/privacy" onClick={() => setOpen(false)} className="hover:text-gray-300 transition-colors">Privacy</Link>
            <span>·</span>
            <Link to="/terms" onClick={() => setOpen(false)} className="hover:text-gray-300 transition-colors">Terms</Link>
          </div>
        </div>
      )}
    </div>
  );
}
